import React, {useEffect, useRef, useState} from 'react';
import {Box, HStack, Text} from '@chakra-ui/react';
import {TPath, useTypedQuery} from '@synako/typed-path';
import {FiltresItemCatalogueGrandCompte} from 'ff-common';
import {HaliRenderOne} from '@synako/haligator';
import {useNomenclature} from '@/haligator-factory';
import {CategoryFilterButton} from './CategoryFilterButton';
import {ChevronLeftIcon} from '@chakra-ui/icons';
import {SearchCatalogueState} from 'api-types/ffconnect';
import {useTranslation} from 'react-i18next';

type Facets = SearchCatalogueState['facets'];

interface CategoryFiltersBarProps {
  filtersHook: ReturnType<
    typeof useTypedQuery<TPath<{Query: FiltresItemCatalogueGrandCompte}>>
  >;
  facets?: Facets;
}

/**
 * Barre des filtres de catégories pour le catalogue grand compte
 */
export const CategoryFiltersBar: React.FC<CategoryFiltersBarProps> = ({
  filtersHook,
  facets,
}) => {
  const {t} = useTranslation();
  const nomenclatureNav = useNomenclature();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Fonction pour vérifier la possibilité de défilement
  const checkScrollability = () => {
    const container = scrollContainerRef.current;
    if (container) {
      // Vérifie si on peut défiler vers la gauche (scroll position > 0)
      setCanScrollLeft(container.scrollLeft > 0);

      // Vérifie si on peut défiler vers la droite
      // (largeur totale de défilement - largeur visible - position actuelle > 1px)
      // Le +1 est une marge d'erreur pour éviter des problèmes avec les valeurs décimales
      const hasMoreToScroll =
        container.scrollWidth - container.clientWidth - container.scrollLeft >
        1;
      setCanScrollRight(hasMoreToScroll);
    }
  };

  // Effet pour vérifier la défilabilité au montage, au changement de taille, et au changement de contenu
  useEffect(() => {
    checkScrollability();

    // Observer les changements de dimension
    const resizeObserver = new ResizeObserver(() => {
      checkScrollability();
    });

    const currentRef = scrollContainerRef.current;

    if (currentRef) {
      resizeObserver.observe(currentRef);
    }

    // Nettoyage
    return () => {
      if (currentRef) {
        resizeObserver.unobserve(currentRef);
      }
      resizeObserver.disconnect();
    };
  }, [filtersHook.params.cat_web]); // Dépendance sur les filtres de catégorie

  // Fonction de changement de filtre actif
  const handleFilterClick = (
    filter: keyof FiltresItemCatalogueGrandCompte,
    value: string,
  ) => {
    filtersHook.change(filter, value);
  };

  // Gestionnaire d'événement de défilement
  const handleScroll = () => {
    checkScrollability();
  };

  return (
    <HaliRenderOne
      nav={nomenclatureNav}
      render={({data: dataNomenclature}) => {
        // Si on est dans une sous-catégorie, récupérer l'info
        const isSubCategory = filtersHook.params.cat_web?.[0]?.includes('-');
        const currentCategoryId = filtersHook.params.cat_web?.[0];
        const subCategory = currentCategoryId
          ? dataNomenclature.web.categories.find(
              (categorie) => categorie.id === currentCategoryId,
            )
          : null;

        // Déterminer les catégories parentes ou de premier niveau
        const parentCategories = dataNomenclature.web.categories.filter(
          (categorie) => !categorie.id_parent,
        );
        const childCategories = currentCategoryId
          ? dataNomenclature.web.categories.filter(
              (categorie) => categorie.id_parent === currentCategoryId,
            )
          : [];

        // Filtrer les catégories à afficher initialement selon le contexte (parent/enfant)
        let displayCategories =
          !currentCategoryId || currentCategoryId.length === 0
            ? parentCategories // Catégories de premier niveau si aucune catégorie sélectionnée
            : childCategories; // Catégories enfants si une catégorie est sélectionnée

        // Si une recherche est active, proposer d'affiner par rayons
        // -> Que la recherche
        // -> Que le premier niveau (pas de catégorie sélectionnée)
        const shouldHighlightFilterCategories =
          facets?.cat_web?.length &&
          filtersHook.params.q &&
          !filtersHook.params.cat_web;

        //On filtre systématiquement les catégories
        const facetCategoryIds = facets?.cat_web ?? [];
        // Conserver uniquement les catégories présentes dans les facettes de la recherche
        displayCategories = displayCategories.filter((category) =>
          facetCategoryIds.includes(category.id),
        );

        return (
          <Box position='relative'>
            {/* Scroll indicator gauche - visible uniquement quand on peut défiler vers la gauche */}
            {canScrollLeft && (
              <Box
                display={{base: 'block', '2xl': 'none'}}
                position='absolute'
                left={0}
                top={0}
                bottom={0}
                width='30px'
                bgGradient='linear(to-r, white, rgba(255,255,255,0.5))'
                zIndex={1}
                pointerEvents='none'
                boxShadow='inset 10px 0 10px -10px rgba(0, 0, 0, 0.1)'
              />
            )}

            {/* Scroll indicator droit - visible uniquement quand on peut défiler vers la droite */}
            {canScrollRight && (
              <Box
                display={{base: 'block', '2xl': 'none'}}
                position='absolute'
                right={0}
                top={0}
                bottom={0}
                width='30px'
                bgGradient='linear(to-l, white, rgba(255,255,255,0.5))'
                zIndex={1}
                pointerEvents='none'
                boxShadow='inset -10px 0 10px -10px rgba(0, 0, 0, 0.1)'
              />
            )}

            {/* Conditionally render the title */}
            {!!shouldHighlightFilterCategories &&
              displayCategories.length > 1 && (
                <Text
                  fontSize='sm'
                  fontWeight='bold'
                  px={{base: 4, md: 6}}
                  mt={4}
                >
                  {t('grand_compte.affiner_par_rayons')}
                </Text>
              )}
            <HStack
              ref={scrollContainerRef}
              spacing={{base: '2', sm: '3', md: '4'}}
              overflowX='auto'
              py={3}
              px={{base: 4, md: 6}}
              scrollBehavior='smooth'
              position='relative'
              pb={{base: 3, md: 4}}
              pt={{base: 3, md: 4}}
              onScroll={handleScroll}
              css={{
                '&::-webkit-scrollbar': {
                  height: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#c0c0c0',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#a0a0a0',
                },
                scrollbarWidth: 'thin',
                scrollbarColor: '#c0c0c0 #f1f1f1',
                WebkitOverflowScrolling: 'touch',
                scrollSnapType: 'x mandatory',
                scrollPadding: '0 10px',
              }}
            >
              {/* Retour */}
              {isSubCategory && subCategory && (
                <CategoryFilterButton
                  label={subCategory.libelle}
                  isActive={true}
                  leftIcon={<ChevronLeftIcon />}
                  onClick={() => {
                    handleFilterClick('cat_web', subCategory.id_parent);
                  }}
                />
              )}

              {/* Afficher les catégories filtrées */}
              {displayCategories.map((filter, index) => (
                <CategoryFilterButton
                  key={index}
                  label={filter.libelle}
                  isActive={
                    filtersHook.params.cat_web?.includes(filter.id) ?? false
                  }
                  onClick={() => {
                    handleFilterClick('cat_web', filter.id);
                  }}
                />
              ))}
            </HStack>
          </Box>
        );
      }}
    />
  );
};
